import React, { useState, useEffect, useMemo } from 'react';
import { Plus, BarChart3, RefreshCcw, AlertCircle, Search, X } from 'lucide-react';
import { apiService, AdminGolfCourse, GolfCourseStats } from '../../services/api';
import GolfCoursesList from './GolfCoursesList';
import GolfCourseForm from './GolfCourseForm';
import DeleteCourseDialog from './DeleteCourseDialog';

function AdminGolfCourses() {
  const [courses, setCourses] = useState<AdminGolfCourse[]>([]);
  const [stats, setStats] = useState<GolfCourseStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [apiTypeFilter, setApiTypeFilter] = useState<'all' | 'cps' | 'chronogolf'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // View mode states
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editingCourse, setEditingCourse] = useState<AdminGolfCourse | null>(null);
  const [deletingCourse, setDeletingCourse] = useState<AdminGolfCourse | null>(null);

  const limit = 10; // Items per page

  // Filter courses based on search term, status, and API type
  const filteredCourses = useMemo(() => {
    return courses.filter(course => {
      const matchesSearch = searchTerm.trim() === '' || 
        course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.location?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = activeFilter === 'all' || 
        (activeFilter === 'active' && course.is_active) ||
        (activeFilter === 'inactive' && !course.is_active);
      
      const matchesApiType = apiTypeFilter === 'all' || course.api_type === apiTypeFilter;
      
      return matchesSearch && matchesStatus && matchesApiType;
    });
  }, [courses, searchTerm, activeFilter, apiTypeFilter]);

  useEffect(() => {
    fetchCourses();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeFilter, apiTypeFilter]);

  const fetchCourses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        limit,
        ...(activeFilter !== 'all' && { is_active: activeFilter === 'active' }),
        ...(apiTypeFilter !== 'all' && { api_type: apiTypeFilter })
      };

      const response = await apiService.getAdminGolfCourses(params);
      setCourses(response.courses);
      setTotalPages(response.pagination.totalPages);
    } catch (error: any) {
      console.error('Failed to fetch courses:', error);
      setError(error.message || 'Failed to fetch golf courses');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await apiService.getGolfCourseStats();
      setStats(statsData);
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateCourse = () => {
    setEditingCourse(null);
    setViewMode('create');
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditCourse = (course: AdminGolfCourse) => {
    setEditingCourse(course);
    setViewMode('edit');
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteCourse = (course: AdminGolfCourse) => {
    setDeletingCourse(course);
  };

  const handleToggleStatus = async (course: AdminGolfCourse) => {
    try {
      await apiService.toggleGolfCourseStatus(course.id);
      fetchCourses(); // Refresh the list
      fetchStats(); // Refresh stats
    } catch (error: any) {
      console.error('Failed to toggle course status:', error);
      setError(error.message || 'Failed to toggle course status');
    }
  };

  const handleFormSuccess = () => {
    setViewMode('list');
    setEditingCourse(null);
    fetchCourses();
    fetchStats();
  };

  const handleFormCancel = () => {
    setViewMode('list');
    setEditingCourse(null);
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteSuccess = () => {
    setDeletingCourse(null);
    fetchCourses();
    fetchStats();
  };

  const handleClearCache = async () => {
    try {
      await apiService.clearGolfCourseCache();
      // Show success message or refresh data
      fetchCourses();
    } catch (error: any) {
      console.error('Failed to clear cache:', error);
      setError(error.message || 'Failed to clear cache');
    }
  };

  if (isLoading && courses.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading golf courses...</p>
        </div>
      </div>
    );
  }

  // Render form view
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <GolfCourseForm
        course={editingCourse}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  // Render list view
  return (
    <div className="space-y-6">
      {/* Header and Stats */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Golf Course Management</h2>
          <p className="mt-1 text-gray-600">Manage golf courses and API configurations</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleClearCache}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <RefreshCcw size={16} />
            <span>Clear Cache</span>
          </button>
          
          <button
            onClick={handleCreateCourse}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            <Plus size={16} />
            <span>Add Course</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Courses</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCourses}</p>
              </div>
              <BarChart3 className="text-blue-600" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Courses</p>
                <p className="text-2xl font-bold text-green-600">{stats.activeCourses}</p>
              </div>
              <BarChart3 className="text-green-600" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CPS API</p>
                <p className="text-2xl font-bold text-purple-600">{stats.apiTypeDistribution.cps}</p>
              </div>
              <BarChart3 className="text-purple-600" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">ChronoGolf API</p>
                <p className="text-2xl font-bold text-orange-600">{stats.apiTypeDistribution.chronogolf}</p>
              </div>
              <BarChart3 className="text-orange-600" size={24} />
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Search Box */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search by name or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border border-gray-300 rounded-md pl-10 pr-8 py-1 text-sm w-64"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value as 'all' | 'active' | 'inactive');
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Type</label>
              <select
                value={apiTypeFilter}
                onChange={(e) => {
                  setApiTypeFilter(e.target.value as 'all' | 'cps' | 'chronogolf');
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                <option value="all">All Types</option>
                <option value="cps">CPS</option>
                <option value="chronogolf">ChronoGolf</option>
              </select>
            </div>
          </div>

          <button
            onClick={fetchCourses}
            className="flex items-center space-x-2 px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <RefreshCcw size={16} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Golf Courses List */}
      <GolfCoursesList
        courses={filteredCourses}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onEdit={handleEditCourse}
        onDelete={handleDeleteCourse}
        onToggleStatus={handleToggleStatus}
      />

      {/* Delete Dialog */}
      {deletingCourse && (
        <DeleteCourseDialog
          course={deletingCourse}
          onSuccess={handleDeleteSuccess}
          onCancel={() => setDeletingCourse(null)}
        />
      )}
    </div>
  );
}

export default AdminGolfCourses;